import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Chip,
  Button,
  LinearProgress,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Collapse,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Header from '../components/Header';
import CRQWKVisualization from '../components/CRQWKVisualization';
import { useJobPolling } from '../hooks/useJobPolling';
import apiService from '../services/api';
import { RENAISSANCE_COLORS } from '../utils';

function EvaluationDetail() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [errorExpanded, setErrorExpanded] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);

  // Poll job status every 2 seconds
  const { job, loading, error, refetch } = useJobPolling(jobId || '', 2000);

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'info';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const calculateTimeElapsed = () => {
    if (!job?.started_at) return 'N/A';
    const start = new Date(job.started_at);
    const end = job.completed_at ? new Date(job.completed_at) : new Date();
    const diff = end.getTime() - start.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const handleDownload = async () => {
    if (!job?.results?.eval_runid) return;

    try {
      setDownloading(true);
      const response = await apiService.downloadEvaluationResult(job.results.eval_runid);

      // Open download URL in new window
      if (response.download_url) {
        window.open(response.download_url, '_blank');
        toast.success('Download started');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to download results';
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleCancel = async () => {
    if (!jobId) return;

    // Confirm cancellation
    if (!window.confirm('Are you sure you want to cancel this evaluation job? This action cannot be undone.')) {
      return;
    }

    try {
      setCancelling(true);
      await apiService.cancelEvaluation(jobId);
      toast.success('Cancellation requested. The job will stop at the next batch.');

      // Refetch job to show updated status
      refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel job';
      toast.error(message);
    } finally {
      setCancelling(false);
    }
  };

  const getQWKColor = (score: number): 'success' | 'warning' | 'error' => {
    if (score >= 0.7) return 'success';
    if (score >= 0.5) return 'warning';
    return 'error';
  };

  if (loading && !job) {
    return (
      <div style={{
        backgroundColor: RENAISSANCE_COLORS.beige,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: RENAISSANCE_COLORS.beige,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">
            {error}
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/evaluations')}
            sx={{ mt: 2 }}
          >
            Back to Dashboard
          </Button>
        </Container>
      </div>
    );
  }

  if (!job) {
    return (
      <div style={{
        backgroundColor: RENAISSANCE_COLORS.beige,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="warning">
            Job not found
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/evaluations')}
            sx={{ mt: 2 }}
          >
            Back to Dashboard
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: RENAISSANCE_COLORS.beige,
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    }}>
      <Header />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flex: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/evaluations')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1" sx={{ flex: 1 }}>
            Evaluation Job Details
          </Typography>
          <Chip
            label={job.status.toUpperCase()}
            color={getStatusColor(job.status)}
            size="medium"
            sx={{ mr: 2 }}
          />
          {(job.status === 'running' || job.status === 'pending') && (
            <Button
              variant="outlined"
              color="error"
              startIcon={cancelling ? <CircularProgress size={20} /> : <CancelIcon />}
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelling...' : 'Cancel Job'}
            </Button>
          )}
        </Box>

        {/* Job ID */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Job ID
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {job.job_id}
          </Typography>
        </Paper>

        {/* Configuration */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Configuration
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Job Type
                </Typography>
                <Typography variant="body1">
                  {job.job_type === 'cr' ? 'Constructed Response (CR)' : 'Written Prose (WP)'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Batch Size
                </Typography>
                <Typography variant="body1">
                  {job.config.batch_size}
                </Typography>
              </Box>

              {/* WP-specific configuration */}
              {job.job_type !== 'cr' && (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Random Sample
                    </Typography>
                    <Typography variant="body1">
                      {job.config.random_sample ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                  {job.config.max_samples && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Max Samples
                      </Typography>
                      <Typography variant="body1">
                        {job.config.max_samples}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* CR-specific configuration */}
              {job.job_type === 'cr' && (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Max Concurrent Requests
                    </Typography>
                    <Typography variant="body1">
                      {job.config.max_concurrent || 150}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Prompt Template
                    </Typography>
                    <Typography variant="body1">
                      {job.config.prompt_template || 'prompt_6'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Temperature
                    </Typography>
                    <Typography variant="body1">
                      {job.config.temperature !== undefined ? job.config.temperature.toFixed(1) : '0.0'}
                    </Typography>
                  </Box>
                  {job.config.sample_enabled && (
                    <>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Sampling Enabled
                        </Typography>
                        <Typography variant="body1">
                          Yes
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Sample Size
                        </Typography>
                        <Typography variant="body1">
                          {job.config.sample_size || 'N/A'}
                        </Typography>
                      </Box>
                    </>
                  )}
                  {job.config.filter_query && (
                    <Box sx={{ gridColumn: 'span 2' }}>
                      <Typography variant="caption" color="text.secondary">
                        Filter Query
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {job.config.filter_query}
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {job.config.dataset_s3_path && (
                <Box sx={{ gridColumn: 'span 2' }}>
                  <Typography variant="caption" color="text.secondary">
                    Dataset
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {job.config.dataset_s3_path}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Progress Section (for running/pending jobs) */}
        {(job.status === 'running' || job.status === 'pending') && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Progress
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">
                    Processing: {job.progress.rows_processed}
                    {job.progress.total_rows ? ` / ${job.progress.total_rows}` : ''} rows
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {job.progress.progress_percentage.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={job.progress.progress_percentage}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Current Batch
                  </Typography>
                  <Typography variant="body1">
                    {job.progress.current_batch}
                    {job.progress.total_batches ? ` / ${job.progress.total_batches}` : ''}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Time Elapsed
                  </Typography>
                  <Typography variant="body1">
                    {calculateTimeElapsed()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Results Section (for completed jobs) */}
        {job.status === 'completed' && job.results && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Results
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Evaluation Run ID
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {job.results.eval_runid}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Rows Processed
                  </Typography>
                  <Typography variant="body1">
                    {job.progress.rows_processed}
                  </Typography>
                </Box>
              </Box>

              {/* WP Results: Show QWK scores as chips */}
              {job.job_type !== 'cr' && job.results.qwk_scores && Object.keys(job.results.qwk_scores).length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    QWK Scores
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {Object.entries(job.results.qwk_scores).map(([trait, score]) => (
                      <Chip
                        key={trait}
                        label={`${trait}: ${score.toFixed(4)}`}
                        color={getQWKColor(score)}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* CR Results: Show comprehensive QWK visualization */}
              {job.job_type === 'cr' && job.results.qwk_data && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    QWK Analysis
                  </Typography>
                  <CRQWKVisualization qwkData={job.results.qwk_data} />
                  <Divider sx={{ my: 3 }} />
                </>
              )}

              <Button
                variant="contained"
                startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? 'Preparing Download...' : 'Download Results'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Cancelled Section (for cancelled jobs) */}
        {job.status === 'cancelled' && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Job Cancelled
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                This job was cancelled by the user.
              </Alert>

              {/* Partial Results for cancelled CR jobs */}
              {job.job_type === 'cr' && job.results?.partial_results_s3_path && (
                <Alert
                  severity="warning"
                  icon={<WarningIcon />}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" gutterBottom>
                    <strong>Partial results are available.</strong>
                  </Typography>
                  <Typography variant="body2">
                    The job processed {job.progress.rows_processed} of {job.progress.total_rows || 'unknown'} rows before cancellation.
                    You can download the partial results below.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      if (job.results?.partial_results_s3_path) {
                        window.open(job.results.partial_results_s3_path, '_blank');
                        toast.info('Opening partial results...');
                      }
                    }}
                    sx={{ mt: 1 }}
                  >
                    Download Partial Results
                  </Button>
                </Alert>
              )}

              {job.progress.rows_processed > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Progress at cancellation: {job.progress.rows_processed}
                    {job.progress.total_rows ? ` / ${job.progress.total_rows}` : ''} rows
                    ({job.progress.progress_percentage.toFixed(1)}%)
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Error Section (for failed jobs) */}
        {job.status === 'failed' && job.error && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="error">
                Error
              </Typography>
              <Alert severity="error" sx={{ mb: 2 }}>
                {job.error.message}
              </Alert>

              {/* Partial Results Warning (for CR jobs) */}
              {job.job_type === 'cr' && job.results?.partial_results_s3_path && (
                <Alert
                  severity="warning"
                  icon={<WarningIcon />}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" gutterBottom>
                    <strong>Partial results are available.</strong>
                  </Typography>
                  <Typography variant="body2">
                    The job failed after processing {job.progress.rows_processed} of {job.progress.total_rows || 'unknown'} rows.
                    You can download the partial results below.
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      if (job.results?.partial_results_s3_path) {
                        window.open(job.results.partial_results_s3_path, '_blank');
                        toast.info('Opening partial results...');
                      }
                    }}
                    sx={{ mt: 1 }}
                  >
                    Download Partial Results
                  </Button>
                </Alert>
              )}

              {job.error.traceback && (
                <Box>
                  <Button
                    onClick={() => setErrorExpanded(!errorExpanded)}
                    endIcon={<ExpandMoreIcon sx={{ transform: errorExpanded ? 'rotate(180deg)' : 'none', transition: '0.3s' }} />}
                    size="small"
                  >
                    {errorExpanded ? 'Hide' : 'Show'} Traceback
                  </Button>
                  <Collapse in={errorExpanded}>
                    <Paper
                      sx={{
                        mt: 1,
                        p: 2,
                        backgroundColor: '#f5f5f5',
                        overflow: 'auto',
                        maxHeight: 300
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{ fontFamily: 'monospace', fontSize: '0.75rem', margin: 0 }}
                      >
                        {job.error.traceback}
                      </Typography>
                    </Paper>
                  </Collapse>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Timestamps */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Timestamps
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {formatDate(job.created_at)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Started
                </Typography>
                <Typography variant="body2">
                  {formatDate(job.started_at)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Completed
                </Typography>
                <Typography variant="body2">
                  {formatDate(job.completed_at)}
                </Typography>
              </Box>
            </Box>
            {job.status === 'completed' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Total Duration
                </Typography>
                <Typography variant="body1">
                  {calculateTimeElapsed()}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>

      <footer style={{
        backgroundColor: RENAISSANCE_COLORS.white,
        padding: '12px 0',
        borderTop: `1px solid ${RENAISSANCE_COLORS.lightgrey}`,
        boxShadow: '0 -2px 10px rgba(0,0,0,0.05)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 20px'
        }}>
          <div style={{ fontSize: '12px' }}>
            Autoscoringevalsvc v0.1
          </div>
          <img
            src="/ren-cropped.svg"
            alt="Renaissance Logo"
            style={{ height: '30px' }}
          />
        </div>
      </footer>
    </div>
  );
}

export default EvaluationDetail;
